import _ from 'lodash';
import { getControllerApiVersion } from './utils';
import { applyFieldDraft, createSettingsDraftStore, restoreSettingsDraft, settingsDraftStorageKey } from './settings_draft';
import { isTankEnabled } from './tank_availability';

export const isInvalidNutrientPumpUnit = (pump) =>
  Number(pump?.type) === 1 && Number(pump?.unit ?? 0) === 0;

export const hasInvalidNutrientPumpUnit = (pumps = []) =>
  Array.isArray(pumps) && pumps.some(isInvalidNutrientPumpUnit);

export const hasInvalidNutrientSettingsUnit = (nutrientSettings = {}) =>
  Object.values(nutrientSettings).some((settings) => hasInvalidNutrientPumpUnit(settings?.pumps));

// Recipe wire fields are integers; remove scaled-slider conversion noise.
export const normalizeNutritionValue = (path, value) =>
  /(?:^|\.)nutrient_settings(?:\[|\.)/.test(path) &&
    typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value)
    : value;

// As in Multi IO, reconnect preserves the controller/zone proxies while the
// editor unmounts. Keep submissions separate from readback and persisted drafts.
const editorSessions = new WeakMap();
export function getNutritionEditorSession(controller, parent) {
  if (!editorSessions.has(controller)) editorSessions.set(controller, new WeakMap());
  const zones = editorSessions.get(controller);
  if (!zones.has(parent)) zones.set(parent, {
    submittedFields: { current: {} },
    previousBaseline: { current: null }
  });
  return zones.get(parent);
}

export const TANK_REFILL_FIELDS = [
  'refill_strategy',
  'fill_timeout_seconds',
  'planned_offset_minutes'
];
export const hasTankRefillSettings = (tank) =>
  tank?.setup_type === 1 &&
  TANK_REFILL_FIELDS.every((key) => Number.isInteger(tank[key])) &&
  tank.refill_strategy !== 255;
export const supportsTankRefill = (controller, tank) =>
  controller.type === 5155 &&
  getControllerApiVersion(controller).localeCompare('BAICA') >= 0 &&
  hasTankRefillSettings(tank);
export const GLOBAL_TANK_SCOPE = 254;
// BAIB is the released timeout-only format; BAIA remains server decode compatibility.
const NUTRITION_TANK_API_MIN = 'BAIC';
export const supportsNutritionTanks = (controller) =>
  getControllerApiVersion(controller).localeCompare(NUTRITION_TANK_API_MIN) >= 0;
// Product limits are narrower than the unsigned-byte wire representation.
export const TANK_LOW_LIMITS = { low_allowance_minutes: 120, low_finish_reserve_minutes: 30 };
export const nutritionScope = (controller, parent) =>
  controller.zones ? Number(parent.zone_id) : 0;
export const hasTankSettings = (nutrition) =>
  nutrition?.schema_version === 2 && Array.isArray(nutrition.tanks);
export const nutritionDraftKey = (controller, scope) => `nutrition_draft:${controller.id}:${scope}`;

export function routeAllowed(tank, routeId) {
  if (routeId === 0) return true;
  return (
    tank?.setup_type === 1 &&
    Number.isInteger(routeId) &&
    routeId >= 1 &&
    routeId <= 31 &&
    Number.isInteger(tank.allowed_route_mask) &&
    tank.allowed_route_mask >= 0 &&
    tank.allowed_route_mask <= 0xffffffff &&
    ((tank.allowed_route_mask >>> routeId) & 1) === 1
  );
}

export function tankRoutes(tank, routes = []) {
  const seen = new Set();
  return routes.filter((route) => {
    if (route.route_id === 0 || seen.has(route.route_id) || !routeAllowed(tank, route.route_id))
      return false;
    seen.add(route.route_id);
    return true;
  });
}

// Editable values deliberately exclude route/setup metadata and all Tank Info.
// Physical tank IDs are keys; array positions never identify a tank draft.
export function nutritionValues(nutrition = {}) {
  return {
    nutrient_settings: (nutrition.nutrient_settings ?? []).map((recipe) => _.cloneDeep(recipe)),
    tanks: Object.fromEntries(
      (nutrition.tanks ?? []).map((tank) => [
        tank.tank_id,
        {
          selected_route_id: tank.selected_route_id,
          low_allowance_minutes: tank.low_allowance_minutes ?? 0,
          low_finish_reserve_minutes: tank.low_finish_reserve_minutes ?? 0,
          ...(hasTankRefillSettings(tank)
            ? Object.fromEntries(TANK_REFILL_FIELDS.map((key) => [key, tank[key]]))
            : {})
        }
      ])
    )
  };
}

export function applyNutritionDraft(nutrition, fields = {}) {
  const values = nutritionValues(nutrition);
  const applicable = Object.fromEntries(
    Object.entries(fields).filter(([path]) => {
      const recipeId = path.match(/^nutrient_settings\[(\d)\]/)?.[1];
      return recipeId === undefined || values.nutrient_settings[recipeId] !== undefined;
    })
  );
  return applyFieldDraft(values, applicable);
}

export function isNutritionField(path) {
  return (
    /^tanks\.[0-7]\.(selected_route_id|low_allowance_minutes|low_finish_reserve_minutes|refill_strategy|fill_timeout_seconds|planned_offset_minutes)$/.test(
      path
    ) ||
    /^nutrient_settings\[[0-3]\]\.(ec_setpoint|ph_setpoint|temp_setpoint|pump_cnt_used|pumps(?:\[\d\]\.(?:type|unit|value|name))?)$/.test(
      path
    )
  );
}

export function migrateNutritionDraft(nutrition, previous = {}) {
  if (previous._nutrition_fields) {
    return Object.fromEntries(
      Object.entries(previous._nutrition_fields).filter(([path]) => isNutritionField(path))
    );
  }
  // Legacy path edits may be sparse arrays/objects. Only complete pump records
  // represent an array replacement; replacing a sparse edit would lose pumps.
  const completePumps = (pumps) =>
    Array.isArray(pumps) &&
    Array.from(pumps).every(
      (pump) =>
        pump && typeof pump === 'object' && ['type', 'unit', 'value'].every((key) => key in pump)
    );
  const restored = restoreSettingsDraft(nutrition, previous, (old, value, key) => {
    if (key !== 'pumps') return undefined;
    if (completePumps(value)) return _.cloneDeep(value);
    // JSON serializes holes in legacy array drafts as null. They stand for
    // untouched pumps, including when deletion metadata is applied afterward.
    const pumps = _.cloneDeep(old ?? []);
    Object.entries(value ?? {}).forEach(([index, pump]) => {
      if (pump != null) pumps[index] = _.merge(pumps[index] ?? {}, _.cloneDeep(pump));
    });
    return pumps;
  });
  const fields = {};
  Object.entries(previous.nutrient_settings ?? {}).forEach(([id, recipe]) => {
    Object.entries(recipe ?? {}).forEach(([key, value]) => {
      const path = `nutrient_settings[${id}].${key}`;
      if (!isNutritionField(path)) return;
      if (key !== 'pumps' || completePumps(value)) fields[path] = _.get(restored, path);
      else
        Object.entries(value ?? {}).forEach(([index, pump]) => {
          Object.keys(pump ?? {}).forEach((field) => {
            const pumpPath = `${path}[${index}].${field}`;
            if (isNutritionField(pumpPath)) fields[pumpPath] = _.get(restored, pumpPath);
          });
        });
    });
  });
  Object.keys(previous._deleted_indicies ?? {}).forEach((path) => {
    if (!/^nutrient_settings\[[0-3]\]\.pumps$/.test(path)) return;
    // Structural edits replace the array and its count together.
    Object.keys(fields).forEach((key) => {
      if (key.startsWith(`${path}[`)) delete fields[key];
    });
    fields[path] = _.get(restored, path);
    const countPath = path.replace(/pumps$/, 'pump_cnt_used');
    fields[countPath] ??= fields[path]?.length;
  });
  return fields;
}

export function createNutritionDraftStore(controller, parent) {
  const store = createSettingsDraftStore(parent, 'nutrition', () =>
    persistNutritionDraft(controller, parent)
  );
  return {
    ...store,
    read: () => store.read()._nutrition_fields ?? {},
    write: (fields) => store.write(Object.keys(fields).length ? { _nutrition_fields: fields } : {})
  };
}

export function loadNutritionDraft(controller, parent) {
  let previous = parent.temp_settings.nutrition;
  if (!previous) {
    try {
      previous = {
        _nutrition_fields: JSON.parse(
          localStorage.getItem(nutritionDraftKey(controller, nutritionScope(controller, parent))) ??
            '{}'
        )
      };
    } catch (_) {
      previous = {};
    }
  }
  createNutritionDraftStore(controller, parent).write(
    migrateNutritionDraft(parent.settings.nutrition, previous)
  );
}

export function nutritionPatch(nutrition, tankInfo, scope, fields) {
  const values = applyNutritionDraft(nutrition, fields);
  const patch = { schema_version: 2 };
  let lastRecipe = -1;
  const tankIds = new Set();
  for (const path of Object.keys(fields)) {
    if (!isNutritionField(path)) throw new Error('nutrition_invalid_settings');
    if (path.startsWith('tanks.')) tankIds.add(Number(path.split('.')[1]));
    else lastRecipe = Math.max(lastRecipe, Number(path.match(/\[(\d)\]/)[1]));
  }
  if (lastRecipe >= 0) {
    if (scope === GLOBAL_TANK_SCOPE || lastRecipe >= (nutrition.nutrient_settings?.length ?? 0))
      throw new Error('nutrition_invalid_settings');
    patch.nutrient_settings = values.nutrient_settings.slice(0, lastRecipe + 1);
    if (
      patch.nutrient_settings.some(
        (recipe) =>
          !Number.isInteger(recipe.pump_cnt_used) ||
          recipe.pump_cnt_used < 0 ||
          recipe.pump_cnt_used > 8 ||
          !Array.isArray(recipe.pumps) ||
          recipe.pumps.length !== recipe.pump_cnt_used
      )
    )
      throw new Error('nutrition_invalid_settings');
  }
  if (tankIds.size) {
    patch.tanks = [...tankIds].map((tank_id) => {
      const tank = nutrition.tanks.find((item) => item.tank_id === tank_id);
      const info = tankInfo?.find((item) => item.tank_id === tank_id);
      const { selected_route_id, low_allowance_minutes, low_finish_reserve_minutes } =
        values.tanks[tank_id];
      if (
        !tank ||
        info?.owner_zone !== scope ||
        Object.entries(TANK_LOW_LIMITS).some(
          ([field, max]) =>
            !Number.isInteger(values.tanks[tank_id][field]) ||
            values.tanks[tank_id][field] < 0 ||
            values.tanks[tank_id][field] > max
        ) ||
        !routeAllowed(tank, selected_route_id) ||
        (selected_route_id !== 0 &&
          (!isTankEnabled(info) ||
            !tankRoutes(tank, nutrition.routes).some(
              (route) => route.route_id === selected_route_id
            )))
      )
        throw new Error('nutrition_invalid_settings');
      const refill = {};
      if (hasTankRefillSettings(tank)) {
        TANK_REFILL_FIELDS.forEach((key) => {
          refill[key] = values.tanks[tank_id][key];
        });
        if (
          !TANK_REFILL_FIELDS.every((key) => Number.isInteger(refill[key])) ||
          refill.refill_strategy < 0 ||
          refill.refill_strategy > 2 ||
          refill.fill_timeout_seconds < 0 ||
          refill.fill_timeout_seconds > 65535 ||
          refill.planned_offset_minutes < -720 ||
          refill.planned_offset_minutes > 720 ||
          (refill.refill_strategy === 2 && refill.fill_timeout_seconds !== 0)
        )
          throw new Error('nutrition_invalid_settings');
      } else if (
        Object.keys(fields).some((key) =>
          TANK_REFILL_FIELDS.some((field) => key === 'tanks.' + tank_id + '.' + field)
        )
      )
        throw new Error('nutrition_invalid_settings');
      return {
        tank_id,
        selected_route_id,
        low_allowance_minutes,
        low_finish_reserve_minutes,
        ...refill
      };
    });
  }
  return patch;
}

export function persistNutritionDraft(controller, parent) {
  const key = nutritionDraftKey(controller, nutritionScope(controller, parent));
  const fields = parent.temp_settings?.nutrition?._nutrition_fields;
  if (fields && Object.keys(fields).length) localStorage.setItem(key, JSON.stringify(fields));
  else localStorage.removeItem(key);
  // Other tabs also persist parent.temp_settings. Remove any older copy from
  // that shared key so a sent/discarded Nutrition draft cannot reappear.
  const legacyKey = settingsDraftStorageKey(parent);
  try {
    const legacy = JSON.parse(localStorage.getItem(legacyKey) ?? '{}');
    if ('nutrition' in legacy) {
      delete legacy.nutrition;
      if (Object.keys(legacy).length) localStorage.setItem(legacyKey, JSON.stringify(legacy));
      else localStorage.removeItem(legacyKey);
    }
  } catch (_) {
    /* An unreadable old cache is not a Nutrition draft. */
  }
}

// Save the complete editable form, including unchanged values, so users can
// resend immediately if a request or the controller's saving message is lost.
export function nutritionSavePatch(nutrition, tankInfo, scope, fields = {}) {
  // Reject stale or forbidden edits before selecting the current owner's tanks.
  nutritionPatch(nutrition, tankInfo, scope, fields);
  const values = applyNutritionDraft(nutrition, fields);
  const allFields = {};
  if (scope !== GLOBAL_TANK_SCOPE) {
    values.nutrient_settings.forEach((recipe, index) => {
      for (const key of ['ec_setpoint', 'ph_setpoint', 'temp_setpoint', 'pump_cnt_used', 'pumps']) {
        allFields[`nutrient_settings[${index}].${key}`] = recipe[key];
      }
    });
  }
  (nutrition.tanks ?? []).forEach((tank) => {
    if (tankInfo?.find((info) => info.tank_id === tank.tank_id)?.owner_zone !== scope) return;
    Object.entries(values.tanks[tank.tank_id]).forEach(([key, value]) => {
      allFields[`tanks.${tank.tank_id}.${key}`] = value;
    });
  });
  return nutritionPatch(nutrition, tankInfo, scope, allFields);
}

export function sendNutritionSave(stateManager, controller, parent, fields) {
  if (controller.connection?.status !== 'good') return false;
  if (!supportsNutritionTanks(controller) || !controller.access_rights?.nutrition?.write)
    return false;
  const scope = nutritionScope(controller, parent);
  if (!(scope === GLOBAL_TANK_SCOPE || (Number.isInteger(scope) && scope >= 0 && scope <= 8)))
    return false;
  let patch;
  try {
    if (
      Object.keys(fields).some((path) =>
        TANK_REFILL_FIELDS.some((field) => path.endsWith('.' + field))
      ) &&
      (controller.type !== 5155 || getControllerApiVersion(controller).localeCompare('BAICA') < 0)
    )
      return false;
    patch = nutritionSavePatch(
      parent.settings.nutrition,
      parent.settings.irrigation_tank_info,
      scope,
      fields
    );
    if (
      patch.tanks?.some((tank) => TANK_REFILL_FIELDS.some((field) => field in tank)) &&
      (controller.type !== 5155 || getControllerApiVersion(controller).localeCompare('BAICA') < 0)
    )
      return false;
  } catch (_) {
    // Revalidate against the live proxy: metadata may change before React has
    // rendered the new snapshot and disabled the button.
    return false;
  }
  try {
    const zoned = Boolean(controller.zones);
    const sent = stateManager.wsManager.sendWSMsg(
      controller,
      'update_setpoints',
      {
        nutrition: patch,
        ...(zoned ? { zone: String(scope) } : {})
      },
      controller.type === 6000,
      zoned
    );
    return Boolean(sent);
  } catch (_) {
    return false;
  }
}
